import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { BookOpen, Plus, Trash2, LogOut, LayoutDashboard, Upload, Download, Filter } from 'lucide-react';

export default function AdminQuestionBank() {
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    question_type: 'mcq',
    options: ['', '', '', ''],
    correct_answer: '',
    marks: 1,
    category_id: ''
  });
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
    fetchQuestions();
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to fetch categories');
    }
  };

  const fetchQuestions = async () => {
    try {
      const url = selectedCategory === 'all' 
        ? `${API}/admin/question-bank`
        : `${API}/admin/question-bank?category_id=${selectedCategory}`;
      const response = await axios.get(url);
      setQuestions(response.data);
    } catch (error) {
      toast.error('Failed to fetch questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Validate form
      if (!questionForm.question_text.trim()) {
        toast.error('Question text is required');
        setSubmitting(false);
        return;
      }
      
      if (!questionForm.correct_answer.trim()) {
        toast.error('Correct answer is required');
        setSubmitting(false);
        return;
      }
      
      const questionData = {
        ...questionForm,
        options: questionForm.question_type === 'mcq' ? questionForm.options.filter(o => o.trim() !== '') : null
      };
      
      console.log('Submitting question:', questionData);
      const response = await axios.post(`${API}/admin/question-bank`, questionData);
      console.log('Response:', response.data);
      
      toast.success('Question added successfully!');
      setShowAddDialog(false);
      setQuestionForm({
        question_text: '',
        question_type: 'mcq',
        options: ['', '', '', ''],
        correct_answer: '',
        marks: 1,
        category_id: ''
      });
      fetchQuestions();
    } catch (error) {
      console.error('Add question error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to add question');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await axios.delete(`${API}/admin/question-bank/${questionId}`);
      toast.success('Question deleted');
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const response = await axios.post(
        `${API}/admin/question-bank/bulk-upload?category_id=${uploadCategory || ''}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(response.data.message);
      setShowUploadDialog(false);
      setUploadFile(null);
      setUploadCategory('');
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = `question_text,question_type,options,correct_answer,marks
What is 2+2?,mcq,2;3;4;5,4,1
Is Earth flat?,true_false,,False,1
Explain photosynthesis,short_answer,,Process of converting light energy,2`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'question_template.csv';
    a.click();
    toast.success('Template downloaded');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50">
      <header className="bg-white border-b border-sky-100 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="h-7 w-7 text-sky-600" />
            <div>
              <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>ESAP Admin</h1>
              <p className="text-sm text-gray-500">Question Bank</p>
            </div>
          </div>
          <Button variant="outline" onClick={handleLogout} className="border-sky-200">
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-6 py-3">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}>Dashboard</Button>
            <Button variant="ghost" onClick={() => navigate('/admin/students')}>Students</Button>
            <Button variant="ghost" onClick={() => navigate('/admin/exams')}>Exams</Button>
            <Button variant="ghost" onClick={() => navigate('/admin/categories')}>Categories</Button>
            <Button variant="ghost" className="text-sky-600">Question Bank</Button>
            <Button variant="ghost" onClick={() => navigate('/admin/results')}>Results</Button>
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Question Bank</h2>
            <p className="text-gray-600">Manage questions</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowUploadDialog(true)} variant="outline" data-testid="upload-btn">
              <Upload className="h-4 w-4 mr-2" />
              Upload
            </Button>
            <Button onClick={() => setShowAddDialog(true)} className="bg-sky-600 hover:bg-sky-700" data-testid="add-question-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Question
            </Button>
          </div>
        </div>

        <div className="mb-6 flex items-center gap-4">
          <Label>Category:</Label>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="spinner mx-auto mb-4"></div>
            <p>Loading...</p>
          </div>
        ) : questions.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No questions yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {questions.map((q, idx) => (
              <Card key={q.id} className="border-sky-100">
                <CardContent className="py-4">
                  <div className="flex justify-between">
                    <div className="flex-1">
                      <p className="font-medium mb-2">{q.question_text}</p>
                      <div className="flex gap-2">
                        <Badge className="bg-blue-100 text-blue-700">{q.question_type}</Badge>
                        {q.category_name && <Badge className="bg-purple-100 text-purple-700">{q.category_name}</Badge>}
                        <Badge className="bg-green-100 text-green-700">{q.marks} marks</Badge>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteQuestion(q.id)} className="text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Question</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddQuestion}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Question</Label>
                <Textarea
                  value={questionForm.question_text}
                  onChange={(e) => setQuestionForm({...questionForm, question_text: e.target.value})}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select value={questionForm.question_type} onValueChange={(v) => setQuestionForm({...questionForm, question_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mcq">MCQ</SelectItem>
                      <SelectItem value="true_false">True/False</SelectItem>
                      <SelectItem value="short_answer">Short Answer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={questionForm.category_id || "uncategorized"} onValueChange={(v) => setQuestionForm({...questionForm, category_id: v === "uncategorized" ? "" : v})}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="uncategorized">No Category</SelectItem>
                      {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {questionForm.question_type === 'mcq' && (
                <div>
                  <Label>Options</Label>
                  {questionForm.options.map((opt, i) => (
                    <Input
                      key={i}
                      value={opt}
                      onChange={(e) => {
                        const newOpts = [...questionForm.options];
                        newOpts[i] = e.target.value;
                        setQuestionForm({...questionForm, options: newOpts});
                      }}
                      placeholder={`Option ${i+1}`}
                      className="mt-2"
                    />
                  ))}
                </div>
              )}
              <div>
                <Label>Marks</Label>
                <Input
                  type="number"
                  min="1"
                  value={questionForm.marks}
                  onChange={(e) => setQuestionForm({...questionForm, marks: parseInt(e.target.value)})}
                  required
                />
              </div>
              <div>
                <Label>Correct Answer</Label>
                <Input
                  value={questionForm.correct_answer}
                  onChange={(e) => setQuestionForm({...questionForm, correct_answer: e.target.value})}
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" className="bg-sky-600" disabled={submitting} data-testid="submit-question-btn">
                {submitting ? 'Adding...' : 'Add Question'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Questions</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFileUpload}>
            <div className="space-y-4 py-4">
              <Button type="button" variant="outline" size="sm" onClick={handleDownloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
              <div>
                <Label>Category</Label>
                <Select value={uploadCategory || "uncategorized"} onValueChange={(v) => setUploadCategory(v === "uncategorized" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="uncategorized">No Category</SelectItem>
                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>CSV File</Label>
                <Input type="file" accept=".csv" onChange={(e) => setUploadFile(e.target.files[0])} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-sky-600">Upload</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
